import React, { useCallback, useState } from 'react';
import { AxiosResponse } from 'axios';
import {
  fetchRegionFeatured,
  fetchRegionAccommodations, fetchRegionMeta, fetchRegionRelated,
} from 'api/region/static';
import { IRegionShort } from 'api/region/types';
import { CurrentLink, CurrentTab } from 'components/region/RegionHeaderNav/constants';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import { useRegionLayoutBubble } from 'page-components/region/_context';
import { IRegionLayout } from 'page-components/region/_layout';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { useFakeQueryWithInitialValue } from 'util/useQueryWithInitialValue';
import { getRegionSlug } from 'util/memcache';

import { ITALodgingReq } from 'components/common/PlanYourTrip/types';
import { IAccommodationPage } from 'api/tripadvisor/types';
import PlanYourTrip from 'components/common/PlanYourTrip/RegionWidget';
import AccommodationsList from 'components/region/AccommodationsList';
import { SpinnerCenteredWrapper } from 'components/Spinner/SpinnerCenteredWrapper';
import { Hero400Error } from 'components/400';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';

export type RegionLodgingPageProps = {
  accommodations: IAccommodationPage | null,
  numResorts: number,
  pathInfo: IRegionShort,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-lodging-page';
export const TEMPLATE_ID = '2.1.5';
export const TEMPLATE_NAME = 'Region Lodging';

export default function RegionLodgingPage({
  accommodations: preAccommodation,
  pathInfo,
}: RegionLodgingPageProps) {
  useRegionLayoutBubble({});

  /**
   * Refresh data with new one
   */
  const {
    current: accommodations,
    hasFreshData: areAccommodationsFresh,
    isFetching,
    isLoading,
  } = useFakeQueryWithInitialValue(
    ['region-fetchRegionAccommodations', pathInfo.uuid],
    async () => fetchRegionAccommodations(pathInfo.uuid),
    preAccommodation,
  );

  /**
   * Lodging widget accommodation data such as in/out date + num of guests and rooms
   */
  const [accommodationParams, setAccommodationParams] = useState<ITALodgingReq>({
    checkIn: '',
    checkOut: '',
    numRooms: 1,
    numAdults: 2,
  });

  const handleChangeAccommodationParams = useCallback(
    (params: ITALodgingReq) => setAccommodationParams(params),
    [setAccommodationParams],
  );

  if (!accommodations && (isFetching || isLoading)) {
    return (
      <SpinnerCenteredWrapper />
    );
  }
  if (!accommodations) {
    return (
      <Hero400Error light />
    );
  }

  return (
    <div className="w-100">
      <PlanYourTrip
        region={pathInfo}
        hasSkiRental={false}
        onLodgingChange={handleChangeAccommodationParams}
        isPartner={false}
      />
      <AccommodationsList
        areAccommodationsFresh={areAccommodationsFresh}
        preAccommodations={accommodations}
        searchParams={accommodationParams}
      />
    </div>
  );
}

export const fetchProps = async ({ region }:{ region: string }): Promise<RegionLodgingPageProps> => {
  const cache: AxiosResponse<IRegionShort> = await assertShouldBeNot404(() => getRegionSlug(region));
  const { uuid } = cache.data;
  const featured = await fetchRegionFeatured(uuid);

  return {
    type: 'region',
    route: 'lodging',
    accommodations: await fetchRegionAccommodations(uuid),
    pathInfo: cache.data,
    numResorts: (await fetchRegionMeta(uuid))?.resorts?.active,
    featured,

    /**
       * Layout
       */
    related: await fetchRegionRelated(uuid),
    navigation: {
      head: HeaderNavCurLink.SNOW_REPORTS,
      tab: CurrentTab.TRIP_PLANNING,
      link: CurrentLink.LODGING,
    },
    adTKey: 'metaregion lodging',
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    hero: {
      title: 'region.lodging.heroTitle',
    },
    meta: {
      redirectTo: cache.data.domain ?? null,
      description: 'MetaRegion.lodging.description',
      title: 'MetaRegion.lodging.title',
    },
    prefetch: false,
  };
};

// This function gets called at build time
// eslint-disable-next-line max-len
export const getRegionLodgingPageStaticProps = wrapStaticProps(fetchProps, true);
export const getRegionLodgingPageServerSideProps = wrapServerSideProps(fetchProps, true);
